// Search helpers for the Coach Content page.
//
// The controller already ships all seven collections in one Inertia payload, so
// searching is a pure function over props — no request, no `q` param. This
// module holds the match rules and the grouped result shape the page renders
// while a search is active. Pure and dependency-free on purpose: no React, no
// browser API, so it stays safe under SSR and unit-testable.

function normalize(query) {
  return typeof query === "string" ? query.trim().toLowerCase() : ""
}

function includes(value, q) {
  return typeof value === "string" && value.toLowerCase().includes(q)
}

// The status bucket a coach-authored course falls into: draft first, then its
// public workflow state. Drives the My Courses status filter.
export function myCourseWorkflowKey(course) {
  if (course.status === "draft") return "draft"
  return course.publicStatus || "private"
}

export function matchesCourse(course, query) {
  const q = normalize(query)
  if (!q) return true
  return (
    includes(course.title, q) ||
    includes(course.category?.name, q) ||
    includes(course.description, q)
  )
}

// Tracks match on their own copy plus the titles of every course they hold.
// `courses` — not `courseNames`, which the serializer truncates to the first
// three and which would leave a track's 4th-and-later course unsearchable.
export function matchesTrack(track, query) {
  const q = normalize(query)
  if (!q) return true
  return (
    includes(track.title, q) ||
    includes(track.description, q) ||
    (track.courses || []).some((c) => includes(c?.title, q))
  )
}

function collectCategories(map, courses) {
  for (const c of courses || []) {
    if (c?.category?.id != null) map.set(String(c.category.id), c.category)
  }
}

// Every distinct category across all seven collections, walking into each
// track's courses. The category filter needs this union while searching —
// per-tab options would filter results the tab never offered.
export function allCategories(collections = {}) {
  const {
    assignedCourses = [],
    assignedTracks = [],
    archivedCourses = [],
    archivedTracks = [],
    catalogTracks = [],
    catalogCourses = [],
    myCourses = [],
  } = collections

  const map = new Map()
  collectCategories(map, assignedCourses)
  collectCategories(map, archivedCourses)
  collectCategories(map, catalogCourses)
  collectCategories(map, myCourses)
  for (const t of [...assignedTracks, ...archivedTracks, ...catalogTracks]) {
    collectCategories(map, t?.courses)
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name))
}

const GROUPS = [
  { key: "assignedTracks", label: "Assigned Tracks", kind: "tracks" },
  { key: "assignedCourses", label: "Assigned Courses", kind: "courses" },
  // #649 — archived content is still the group's, so search never says "no
  // results" for it. The page marks these rows with an Archived badge.
  { key: "archivedTracks", label: "Archived Tracks", kind: "tracks" },
  { key: "archivedCourses", label: "Archived Courses", kind: "courses" },
  { key: "catalogTracks", label: "Available Tracks", kind: "tracks" },
  { key: "catalogCourses", label: "Available Courses", kind: "courses" },
  { key: "myCourses", label: "My Courses", kind: "courses" },
]

function courseMatchesCategory(course, categoryId) {
  return !categoryId || String(course.category?.id) === categoryId
}

function trackMatchesCategory(track, categoryId) {
  if (!categoryId) return true
  return (track.courses || []).some((c) => String(c?.category?.id) === categoryId)
}

// Ordered groups mirroring the tab bar, empty ones omitted. No dedup across
// groups: a course a coach authored and assigned belongs in both My Courses and
// Assigned Courses because its row actions differ. No cap and no re-sort —
// each group keeps the server's order.
export function buildSearchGroups(collections = {}, options = {}) {
  const source = {
    assignedCourses: collections.assignedCourses || [],
    assignedTracks: collections.assignedTracks || [],
    archivedCourses: collections.archivedCourses || [],
    archivedTracks: collections.archivedTracks || [],
    catalogTracks: collections.catalogTracks || [],
    catalogCourses: collections.catalogCourses || [],
    myCourses: collections.myCourses || [],
  }
  const { query, categoryId = "", myCourseStatusFilter = "all" } = options

  return GROUPS.map((group) => {
    const items = source[group.key].filter((item) => {
      if (group.kind === "tracks") {
        return matchesTrack(item, query) && trackMatchesCategory(item, categoryId)
      }
      if (!matchesCourse(item, query) || !courseMatchesCategory(item, categoryId)) return false
      if (group.key === "myCourses" && myCourseStatusFilter !== "all") {
        return myCourseWorkflowKey(item) === myCourseStatusFilter
      }
      return true
    })
    return { ...group, items, count: items.length }
  }).filter((group) => group.count > 0)
}
